import React, { useCallback, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { ReviewCompanionshipType } from 'api/review'

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  margin-top: ${rem(16)};
  color: ${props => props.theme.palette.brand.primary.normal};
`

function toggleSelectedSet<T>(selectedSet: Set<T>, value: T) {
  const nextSelectedSet = new Set([...selectedSet.values()])

  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }

  return nextSelectedSet
}

type Option = {
  label: string
  value: string
}

function generateOptionItems(options: Array<Option>, localStates: Set<string>) {
  return options.map((item) => ({
    label: item.label,
    value: item.value,
    checked: localStates.has(item.value),
  }))
}

export interface UserReviewFiltersModalResult {
  ratings: Array<string>
  travellers: Array<ReviewCompanionshipType>
  months: Array<string>
}

interface LocalFilters {
  ratings?: Array<string>
  travellers?: Array<ReviewCompanionshipType>
  months?: Array<string>
}

const availableFilters = {
  ratings: [
    { label: 'Exceptional: 9+', value: 'exceptional' },
    { label: 'Good: 7.0 - 8.9', value: 'good' },
    { label: 'Okay: 5.0 - 6.9', value: 'okay' },
    { label: 'Poor: 3.0 - 4.9', value: 'poor' },
    { label: 'Very poor: 1.0 - 2.9', value: 'very-poor' },
  ],
  travellers: [
    { label: 'Solo travellers', value: 'alone' },
    { label: 'Couples', value: 'partner' },
    { label: 'Families', value: 'family' },
    { label: 'Friends', value: 'friends' },
    { label: 'Business travellers', value: 'work' },
  ],
  months: [
    { label: 'Dec - Feb', value: '12,1,2' },
    { label: 'Mar - May', value: '3,4,5' },
    { label: 'Jun - Aug', value: '6,7,8' },
    { label: 'Sep - Nov', value: '9,10,11' },
  ],
}

interface Props {
  existingFilters?: LocalFilters
  isLoading?: boolean
}

function UserReviewFiltersModal(props: Props) {
  const {
    existingFilters,
    isLoading,
  } = props

  const { resolve } = useModalElementContext<UserReviewFiltersModalResult>()
  const [localRatings, setLocalRatings] = useState<Set<string>>(
    existingFilters?.ratings ? new Set(existingFilters.ratings) : new Set(),
  )
  const [localCompanionship, setLocalCompanionship] = useState<Set<ReviewCompanionshipType>>(
    existingFilters?.travellers ? new Set(existingFilters.travellers) : new Set(),
  )
  const [localMonths, setLocalMonths] = useState<Set<string>>(
    existingFilters?.months ? new Set(existingFilters.months) : new Set(),
  )

  const onFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    resolve({
      ratings: [...localRatings],
      travellers: [...localCompanionship],
      months: [...localMonths],
    })
  }

  const onRatingsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextRatings = toggleSelectedSet(localRatings, e.target.value)
    setLocalRatings(nextRatings)
  }, [localRatings])

  const onCompanionshipChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextCompanionship = toggleSelectedSet(localCompanionship, e.target.value as ReviewCompanionshipType)
    setLocalCompanionship(nextCompanionship)
  }, [localCompanionship])

  const onMonthsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextMonths = toggleSelectedSet(localMonths, e.target.value)
    setLocalMonths(nextMonths)
  }, [localMonths])

  const ratingsItems = useMemo(
    () => generateOptionItems(availableFilters.ratings, localRatings),
    [localRatings],
  )

  const companionshipItems = useMemo(
    () => generateOptionItems(availableFilters.travellers, localCompanionship),
    [localCompanionship],
  )

  const monthsItems = useMemo(
    () => generateOptionItems(availableFilters.months, localMonths),
    [localMonths],
  )

  const onRatingsReset = useCallback(() => {
    setLocalRatings(new Set())
  }, [])

  const onCompanionshipReset = useCallback(() => {
    setLocalCompanionship(new Set())
  }, [])

  const onMonthsReset = useCallback(() => {
    setLocalMonths(new Set())
  }, [])

  const onFormReset = useCallback(() => {
    onRatingsReset()
    onCompanionshipReset()
    onMonthsReset()
  }, [onRatingsReset, onCompanionshipReset, onMonthsReset])

  return (
    <ModalBase mode="drawer">
      <form onSubmit={onFilterSubmit}>
        <ModalHeader title="Filters" />
        <ModalBody>
          <ModalContent>
            {isLoading && (
              <CSSBreakpoint min="tablet">
                <ColourfulLoadingAnimation size={8} />
              </CSSBreakpoint>
            )}
            <FilterPanelFilterGroup
              title="Ratings"
              onReset={onRatingsReset}
              canReset={localRatings.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="ratings"
                items={ratingsItems}
                onChange={onRatingsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title="Travellers"
              onReset={onCompanionshipReset}
              canReset={localCompanionship.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="travellers"
                items={companionshipItems}
                onChange={onCompanionshipChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title="Months"
              onReset={onMonthsReset}
              canReset={localMonths.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="months"
                items={monthsItems}
                onChange={onMonthsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: 'View reviews',
            type: 'submit',
            'data-testid': 'view-reviews-filter-button',
          }}
          secondaryActionProps={{
            children: 'Reset all',
            onClick: onFormReset,
          }}
        />
      </form>
    </ModalBase>
  )
}

export default UserReviewFiltersModal
